/**
 * Shared title-left / trailing-right layout for Day allday rows and list rows
 * (e.g. Plan Next Actions). Week/Month grid title sx stays in buildGridTaskTitleSx.
 */

export const TASK_ROW_TITLE_MIN_WIDTH_PX = 40;

/** Fixed duration column (room for ∑ / dash prefix + e.g. 1h 30m). */
export const TASK_ROW_DURATION_COL_WIDTH = '8.5ch';

/** Per-chip cap in dense list rows — see TagChip `min(14ch, 100%)`. */
export const TASK_ROW_PILLS_COL_WIDTH = '14ch';

/** Outer flex row: title grows; trailing group stays on the first line. */
export function buildTaskRowTitleTrailingRowSx() {
  return {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    columnGap: '4px',
    minWidth: 0,
    width: '100%',
  };
}

/** Title takes remaining space; wraps or truncates without shoving trailing. */
export function buildTaskRowTitleSx() {
  return {
    flex: '1 1 auto',
    minWidth: TASK_ROW_TITLE_MIN_WIDTH_PX,
    overflow: 'hidden',
  };
}

/**
 * Duration / project pill / tags — fixed trailing group pinned to the right edge.
 * @param {{ marginLeftAuto?: boolean }} [options]
 */
export function buildTaskRowTrailingSx({ marginLeftAuto = true } = {}) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    flex: '0 0 auto',
    flexShrink: 0,
    minWidth: 0,
    maxWidth: '100%',
    ...(marginLeftAuto ? { marginLeft: 'auto' } : {}),
  };
}

/** Pill/tags column left of duration — shrinks with content, right-aligned. */
export function buildTaskRowPillsColSx() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0.5,
    flex: '0 1 auto',
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  };
}

/** Duration column — fixed width, tabular nums, right-aligned. */
export function buildTaskRowDurationColSx() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: `0 0 ${TASK_ROW_DURATION_COL_WIDTH}`,
    width: TASK_ROW_DURATION_COL_WIDTH,
    minWidth: TASK_ROW_DURATION_COL_WIDTH,
    maxWidth: TASK_ROW_DURATION_COL_WIDTH,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  };
}

/** Convenience bundle for callers that need all three. */
export function buildTaskRowTitleTrailingSx() {
  return {
    row: buildTaskRowTitleTrailingRowSx(),
    title: buildTaskRowTitleSx(),
    trailing: buildTaskRowTrailingSx(),
    pillsCol: buildTaskRowPillsColSx(),
    durationCol: buildTaskRowDurationColSx(),
  };
}
